import { memo, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ForwardToInboxOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { isNil } from "lodash";
import Flx from "../../../../components/layout/Flx";
import LoadingFallback from "../../../../components/LoadingFallback";

import GenericEmailEditor from "../../../../components/Email/GenericEmailEditor";
import LoadingContent from "../../../../components/LoadingContent";
import TabContent from "../../../../components/navigation/TabContent";
import ToggleTabSwitcher from "../../../../components/navigation/ToggleTabSwitcher";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import { sidebarSetStateValue } from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataEmailTemplatesSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import TemplateSelector from "../../tabs/LoanDrilldownCommunicationTab/components/TemplateSelector";
import DrilldownSidebarPane from "../DrilldownSidebarPane";
import GenericBlockEmailEditor from "../../../../components/Email/GenericBlockEmailEditor";

const OBJECT_TYPE_ITEM_LABEL = "Email Composer";

const DrilldownSidebarEmailComposer = memo(() => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const state = useSelector((state) => state.sidebar?.state);
  // const params = useSelector((state) => state.sidebar?.params);

  const templateData = state;
  const [template, setTemplate] = useState(null);

  const template_name = useMemo(() => templateData?.name, [templateData?.name]);

  const title = useMemo(() => {
    return templateData?.template_display_name || OBJECT_TYPE_ITEM_LABEL;
  }, [templateData?.template_display_name]);

  const { previewEmailTemplate } = useLoanTermsHook();
  const dispatch = useDispatch();

  useEffect(() => {
    if (template_name === "custom") {
      setTemplate({
        template_name: "custom",
        subject: templateData?.subject,
        html_body: templateData?.body,
      });
      return;
    }
    if (isNil(template)) {
      previewEmailTemplate({
        templateName: templateData?.name,
        dealId: loanDrilldown?._id,
        onSuccessFn: (tempData) => {
          setTemplate(tempData || {});
        },
        onFailFn: () => {
          setTemplate("Unable to load template preview");
        },
      });
    } else {
      setTemplate(null);
      previewEmailTemplate({
        templateName: templateData?.name,
        dealId: loanDrilldown?._id,
        onSuccessFn: (tempData) => {
          setTemplate(tempData || {});
        },
        onFailFn: () => {
          setTemplate("Unable to load template preview");
        },
      });
    }
  }, [template_name]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <DrilldownSidebarPane
        title={title}
        icon={<ForwardToInboxOutlined className="thin" />}
        initialWidth={"600px"}
        bodyPadding={0}
        // bodySx={{ pt: 2 }}
      >
        <Content template={template} />
      </DrilldownSidebarPane>
    </Suspense>
  );
});

const TAB_TYPES = {
  SEND: "Email Composer",
  TEMPLATES: "Templates",
};

const Content = ({ template }) => {
  const [tab, setTab] = useState(TAB_TYPES.SEND);
  const dispatch = useDispatch();
  const { getEmailTemplateNames } = useLoanTermsHook();
  const emailTemplates = useSelector(
    (state) => state.loanFetchedData?.emailTemplates,
  );

  const onTemplateSelect = (template) => {
    if (!template) return;
    setTab(TAB_TYPES.SEND);
    dispatch(sidebarSetStateValue({ ...template }, {}));
    // navigate(
    //   `/loan/${loanDrilldown?.loanNumber}/communication/${template.slug}`,
    // );
  };

  useEffect(() => {
    if (isNil(emailTemplates)) {
      getEmailTemplateNames({
        onSuccessFn: (d) => {
          const TEMP_NAME_MAP = {
            insurance_request: "Send Homeowners Insurance Request",
            insurance_quote_request: "Send Insurance Quote Request",
            title_request: "Send Title Request",
            title_order_request: "Send Title Order Request",
          };
          const templateList = d?.templateNames || d;
          const convertedToForm = templateList?.map((n) => {
            return {
              template_display_name: TEMP_NAME_MAP[n],
              // display_name: TEMP_NAME_MAP[n],
              name: n,
              description: `Description for ${n}`,
              subject: `Subject for ${n}`,
            };
          });
          dispatch(loanFetchedDataEmailTemplatesSet(convertedToForm));
          // setTemplates(convertedToForm || []);
        },
      });
    }
  }, []);

  if (isNil(template)) {
    return <LoadingContent>Loading template...</LoadingContent>;
  }

  return (
    <Flx
      column
      sx={{
        // p: 2,
        // pb: 8,
        flexGrow: 1,
        height: "100%",
        overflow: "hidden",
        borderTop: "1px solid rgb(224, 221, 213)",
        background: "#fafaf8",
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid rgb(224, 221, 213)",
          pl: 1.5,
          background: "#ffffff",
        }}
      >
        <ToggleTabSwitcher
          tabs={[
            TAB_TYPES.SEND,
            TAB_TYPES.TEMPLATES,
            // TAB_TYPES.TEMPLATES,
          ]}
          value={tab}
          variant={"underline"}
          onChange={setTab}
        />
      </Box>

      <Box
        sx={{
          p: 2,
          pb: 8,

          height: "100%",
          overflowY: "auto",
        }}
      >
        <TabContent activeTab={tab} tabKey={TAB_TYPES.SEND}>
          {template?.template_name === "custom" ? (
            <GenericBlockEmailEditor template={template} />
          ) : (
            <GenericEmailEditor template={template} />
          )}
        </TabContent>
        <TabContent activeTab={tab} tabKey={TAB_TYPES.TEMPLATES}>
          <TemplateSelector
            templates={emailTemplates}
            // activeTemplate={activeTemplate}
            // activeRowKeyValue={templateData?.name}
            onSelect={onTemplateSelect}
          />
        </TabContent>
      </Box>
    </Flx>
  );
};

const EX_TEMPLATE_FETCH = {
  template_name: "title_request",
  subject: "Title Request - LQ27800",
  html_body:
    '<div>\n\t<p>Title Team,</p>\n\t<p>Please confirm receipt and provide E&amp;O, Wire Instructions, and CPL with our mortgagee and loan number within 24 hours of this request.</p>\n\t<p>\n\t\tWe are the Lender, <strong>N/A</strong> in the above referenced loan transaction.\n\t\tWe will need title commitment for the Subject Property showing N/A.\n\t\tIt must include 24 month Chain of Title, Judgement/Lien Search on entity and guarantors for the county and state of both the property and entity, and OFAC Search.\n\t</p>\n\t<p>\n\t\tI will also need:<br>\n\t\t1. Commitment<br>\n\t\t2. A copy of the Deed - we must have the signed version as well<br>\n\t\t3. Prelim HUD\n\t</p>\n\n\t<h6 style="margin:12px 0 6px 0; font-size:12px; text-transform:uppercase; letter-spacing:.04em;">Loan Terms</h6>\n\t<ul>\n\t\t<li><strong>Mortgagee Clause:</strong> N/A</li>\n\t\t<li><strong>Expected Closing Date:</strong> 2026-03-04 21:34:29.042000</li>\n\t\t<li><strong>Loan Program:</strong> DSCR</li>\n\t\t<li><strong>Transaction Type:</strong> Purchase</li>\n\t\t<li><strong>Loan Number:</strong> LQ27800</li>\n\t\t<li><strong>Total Loan Amount:</strong> 500000.0</li>\n\t\t<li><strong>Interest Rate:</strong> 0.0</li>\n\t\t<li><strong>Borrowing Entity Name:</strong> TBD</li>\n\t\t<li><strong>Flood Zone:</strong> N/A</li>\n\t</ul>\n\n\t<h6 style="margin:12px 0 6px 0; font-size:12px; text-transform:uppercase; letter-spacing:.04em;">Subject Properties</h6>\n\t<ul>\n\t\t\n\t\t<li>\n\t\t\t<div><strong>Property Address:</strong> 125 Carley Avenue, Huntington, NY 11743</div>\n\t\t\t<div><strong>Property Type:</strong> Multifamily</div>\n\t\t</li>\n\t\t\n\t</ul>\n\n\t\n\t\n\t\n\t\n\t\n\t\n\n\t<h6 style="margin:12px 0 6px 0; font-size:12px; text-transform:uppercase; letter-spacing:.04em;">Guarantor</h6>\n\t<ul>\n\t\t<li><strong>Guarantor (1):</strong> N/A</li>\n\t\t<li><strong>Guarantor (1) Contact Information:</strong> N/A</li>\n\t</ul>\n\n\t<h6 style="margin:12px 0 6px 0; font-size:12px; text-transform:uppercase; letter-spacing:.04em;">Loan Team</h6>\n\t<ul>\n\t\t<li><strong>Loan Coordinator:</strong> N/A</li>\n\t\t<li><strong>Closer:</strong> N/A</li>\n\t\t<li><strong>Legal Lender Counsel:</strong> N/A</li>\n\t</ul>\n\n\t<h6 style="margin:12px 0 6px 0; font-size:12px; text-transform:uppercase; letter-spacing:.04em;">Title / Settlement Agent Contacts</h6>\n\t<ul>\n\t\t\n\t\t\n\t\t<li>\n\t\t\t<div><strong>Title Agent:</strong> Jimmy Oliva</div>\n\t\t\t<div><strong>Email:</strong> olivajames110@gmail.com</div>\n\t\t\t<div><strong>Phone:</strong> 6314563373</div>\n\t\t</li>\n\t\t\n\t\t\n\t\t\n\t\t\n\t</ul>\n\n\t<h6 style="margin:12px 0 6px 0; font-size:12px; text-transform:uppercase; letter-spacing:.04em;">Fees for Closing Statement / HUD Preparation</h6>\n\t<ul>\n\t\t\n\t\t\n\t\t\t\n\t\t\t\t\n\t\t\t\t<li>\n\t\t\t\t\t<strong>Per Diem Interest:</strong>\n\t\t\t\t\t\n\t\t\t\t\t\t0.0\n\t\t\t\t\t\n\t\t\t\t</li>\n\t\t\t\n\t\t\n\t\t\t\n\t\t\t\t\n\t\t\t\t<li>\n\t\t\t\t\t<strong>Origination Points:</strong>\n\t\t\t\t\t\n\t\t\t\t\t\t0.0\n\t\t\t\t\t\n\t\t\t\t</li>\n\t\t\t\n\t\t\n\t\t\n\t\t\n\t\t\n\n\t\t\n\t</ul>\n\n\t<p>Once we have these items our counsel will reach out if anything further is needed.</p>\n\t<p>Thank you,\n\t<br>Processing Team</p>\n</div>',
  text_body:
    "\nTitle Team,\nPlease confirm receipt and provide E&O, Wire Instructions, and CPL with our mortgagee and loan number within 24 hours of this request.\n\n\t\tWe are the Lender, N/A in the above referenced loan transaction.\n\t\tWe will need title commitment for the Subject Property showing N/A.\n\t\tIt must include 24 month Chain of Title, Judgement/Lien Search on entity and guarantors for the county and state of both the property and entity, and OFAC Search.\n\t\n\n\t\tI will also need:\n\t\t1. Commitment\n\t\t2. A copy of the Deed - we must have the signed version as well\n\t\t3. Prelim HUD\n\t\nLoan Terms\n\nMortgagee Clause: N/A\nExpected Closing Date: 2026-03-04 21:34:29.042000\nLoan Program: DSCR\nTransaction Type: Purchase\nLoan Number: LQ27800\nTotal Loan Amount: 500000.0\nInterest Rate: 0.0\nBorrowing Entity Name: TBD\nFlood Zone: N/A\n\nSubject Properties\n\n\nProperty Address: 125 Carley Avenue, Huntington, NY 11743\nProperty Type: Multifamily\n\n\nGuarantor\n\nGuarantor (1): N/A\nGuarantor (1) Contact Information: N/A\n\nLoan Team\n\nLoan Coordinator: N/A\nCloser: N/A\nLegal Lender Counsel: N/A\n\nTitle / Settlement Agent Contacts\n\n\nTitle Agent: Jimmy Oliva\nEmail: olivajames110@gmail.com\nPhone: 6314563373\n\n\nFees for Closing Statement / HUD Preparation\n\n\nPer Diem Interest:\n\t\t\t\t\t\n\t\t\t\t\t\t0.0\n\t\t\t\t\t\n\t\t\t\t\n\nOrigination Points:\n\t\t\t\t\t\n\t\t\t\t\t\t0.0\n\t\t\t\t\t\n\t\t\t\t\n\nOnce we have these items our counsel will reach out if anything further is needed.\nThank you,\n\tProcessing Team\n",
  sent_date_utc: "2026-03-04T16:27:24.296050+00:00",
};

const EX_SENT_PAYLOAD = {
  toRecipients: ["olivajames110@gmail.com"],
  ccRecipients: [],
  subject: "Insurance Request - LQ27800",
  body: '<p>Insurance Team, <strong>(Change)</strong></p><p>We would like to request an insurance policy for <strong>Dev Jimmy Test Loan - 1119</strong> for the <strong>Purchase</strong> transaction for the below list of properties. Please check both the "loss payee" and "mortgagee" clauses on the EOI. The projected closing date for the loan is <strong>2026-03-04 21:34:29.042000</strong>.</p><h6>List of Properties</h6><ul><li><p><strong>Property Address:</strong> 125 Carley Avenue, Huntington, NY 11743</p><p><strong>Property Type:</strong> Multifamily</p></li></ul><p></p><h6>Mortgagee Clause</h6><p>N/A ISAOA/ATIMA<br>290 Post Road West, Suite 100 Westport, CT 06880</p><p>--</p><h6>Loan Information</h6><ul><li><p><strong>Borrower Name:</strong> Dev Jimmy Test Loan - 1119</p></li><li><p><strong>Loan Program:</strong> DSCR</p></li><li><p><strong>Loan ID:</strong> LQ27800</p></li></ul><h6>Borrower Contact Information</h6><ul><li><p><strong>Name:</strong> Frankie Lindor</p><p><strong>Email:</strong> franklind@mets.com</p><p><strong>Phone Number:</strong> 5154234005</p><p><strong>Ownership:</strong> %</p></li><li><p><strong>Name:</strong> Juan Soto</p><p><strong>Email:</strong> jsoto@nym.org</p><p><strong>Phone Number:</strong> 5145512622</p><p><strong>Ownership:</strong> %</p></li></ul><h6>Disclaimers</h6><p>Purchase transactions may be delayed due to various circumstances. An insurance quote will be acceptable to verify costs and coverage, but an EOI and invoice will be required prior to closing.</p><p>Thank you,<br>Lendmarq Processing Team</p>',
  attachmentIds: [],
  contentType: "html",
};
export default DrilldownSidebarEmailComposer;
